/**
 * Social channels
 * @enum {string}
 * @readonly
 */
export const SOCIAL_CHANNEL = {
  facebook: 'facebook',
  instagram: 'instagram',
  linkedin: 'linkedin',
  linkedinSocial: 'linkedin_social',
  twitter: 'twitter',
  google: 'google',
  youtube: 'youtube',
  pinterest: 'pinterest',
  facebookPages: 'facebook_pages',
};

/**
 * Native channels
 * @enum {string}
 * @readonly
 */
export const NATIVE_CHANNEL = {
  taboola: 'taboola',
  outbrain: 'outbrain',
  revcontent: 'revcontent',
  verizon: 'verizon',
  amazon: 'amazon',
  walmart: 'walmart',
  teads: 'teads',
};

/**
 * Reviews channels
 * @enum {string}
 * @readonly
 */
export const REVIEWS_CHANNEL = {
  trustpilot: 'trustpilot',
  amazon: 'amazon',
  g2: 'g2',
};

/**
 * Website channels
 * @enum {string}
 * @readonly
 */
export const WEBSITE_CHANNEL = {
  homepage: 'home_page',
  productPage: 'product_page',
  landingPage: 'landing_page',
  competitors: 'competitors',
  thoughtLeadership: 'thought_leadership',
  other: 'other',
};

/**
 * Emails channels
 * @enum {string}
 * @readonly
 */
export const EMAILS_CHANNEL = {
  hubspot: 'hubspot',
};

/**
 * Native Platform channels
 * @enum {string}
 * @readonly
 */
export const PLATFORM_CHANNEL = {
  projectWtm: 'project_wtm',
  blogUrls: 'blog_urls',
};

/**
 * Channels combined enum
 * @enum {string}
 * @readonly
 */
export const CHANNEL = {
  ...SOCIAL_CHANNEL,
  ...NATIVE_CHANNEL,
  ...REVIEWS_CHANNEL,
  ...WEBSITE_CHANNEL,
  ...EMAILS_CHANNEL,
  ...PLATFORM_CHANNEL,
};

const CHANNEL_PROPS = {
  [CHANNEL.facebook]: {
    label: 'Facebook',
    performanceLabel: 'Meta ads',
    talkingPointLabel: 'Meta ads',
    performanceShortLabel: 'Meta',
    description:
      'Learn what talking points actually worked in your Meta ads and train custom models that generate high performing ads that are personalized for your business.',
  },
  [CHANNEL.facebookPages]: {
    label: 'Facebook',
    performanceLabel: 'Facebook page',
    talkingPointLabel: 'Facebook page',
    performanceShortLabel: 'Facebook',
    description:
      'Learn what talking points actually worked in your Facebook posts and train custom models that generate high performing posts that are personalized for your business.',
  },
  [CHANNEL.instagram]: {
    label: 'Instagram',
    performanceLabel: 'Instagram account',
    talkingPointLabel: 'Instagram',
    performanceShortLabel: 'Instagram',
    description:
      'Learn what talking points actually worked in your Instagram captions and train custom models that generate high performing posts that are personalized for your business.',
  },
  [CHANNEL.twitter]: {
    label: 'X',
  },
  [CHANNEL.pinterest]: {
    label: 'Pinterest',
  },
  [CHANNEL.linkedin]: {
    label: 'LinkedIn',
    performanceLabel: 'LinkedIn ads',
    talkingPointLabel: 'LinkedIn ads',
    performanceShortLabel: 'LinkedIn',
    description:
      'Learn what talking points actually worked in your LinkedIn ads and train custom models that generate high performing ads that are personalized for your business.',
  },
  [CHANNEL.linkedinSocial]: {
    label: 'LinkedIn',
    performanceLabel: 'LinkedIn page',
    talkingPointLabel: 'LinkedIn page',
    performanceShortLabel: 'LinkedIn',
    description:
      'Learn what talking points actually worked in your LinkedIn page and train custom models that generate high performing posts that are personalized for your business.',
  },
  [CHANNEL.google]: {
    label: 'Google',
    performanceLabel: 'Google ads',
    talkingPointLabel: 'Google ads',
    performanceShortLabel: 'Google',
    description:
      'Learn what talking points actually worked in your Google ads and train custom models that generate high performing ads that are personalized for your business.',
  },
  [CHANNEL.youtube]: {
    label: 'YouTube',
  },
  [CHANNEL.taboola]: {
    label: 'Taboola',
  },
  [CHANNEL.outbrain]: {
    label: 'Outbrain',
  },
  [CHANNEL.revcontent]: {
    label: 'Revcontent',
  },
  [CHANNEL.teads]: {
    label: 'Teads',
  },
  [CHANNEL.verizon]: {
    label: 'Verizon Media',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
  },
  [CHANNEL.walmart]: {
    label: 'Walmart',
  },
  [CHANNEL.trustpilot]: {
    label: 'Trustpilot',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Trustpilot reviews',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Amazon reviews',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.g2]: {
    label: 'G2',
    performanceLabel: 'G2 reviews',
    description: '',
  },
  [CHANNEL.homepage]: {
    label: 'Homepage',
    talkingPointLabel: 'Webpage',
    description:
      'Connect your homepage for more effective, on-brand content generation. Use it as a source to set your brand voice and access talking points that resonate most with your audience.',
  },
  [CHANNEL.landingPage]: {
    label: 'Landing page',
    talkingPointLabel: 'Webpage',
    description:
      'Connect your landing pages for more effective, on-brand content generation. Use it as a source to set your brand voice and access talking points that resonate most with your audience.',
  },
  [CHANNEL.competitors]: {
    label: 'Competitor',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Competitor website',
    description:
      'Uncover your competitors brand voice and discover their most impactful words for a better understanding.',
  },
  [CHANNEL.thoughtLeadership]: {
    label: 'Thought leadership',
    talkingPointLabel: 'Webpage',
    description:
      'Get powerful talking points from industry thought leadership (articles, blogs...) to make your content more engaging.',
  },
  [CHANNEL.productPage]: {
    label: 'Product information',
    talkingPointLabel: 'Webpage',
    description:
      'Get powerful talking points from industry product page (articles, blogs...) to make your content more engaging.',
  },
  [CHANNEL.other]: {
    label: 'Webpage',
    performanceLabel: 'Any webpage (URL)',
    talkingPointLabel: 'Webpage',
    performanceShortLabel: 'Webpage',
    description: '',
  },
  [CHANNEL.hubspot]: {
    label: 'Email',
    performanceLabel: 'HubSpot emails',
    talkingPointLabel: 'Email',
    performanceGroupLabel: 'Email',
    performanceShortLabel: 'Email',
    description:
      'Learn what talking points actually worked in your emails and train custom models that generate high performing emails that are personalized for your business.',
  },
  [CHANNEL.projectWtm]: {
    label: 'Website automation',
    description:
      'Analyze your website content with instant predictive performance scores. Add Anyword JS to your page to test and improve copy in real time.',
  },
  [CHANNEL.blogUrls]: {
    label: 'Blog posts and articles',
  },
};

export const getChannelLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return props?.label || '';
};

export const getTalkingPointChannelLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return props?.talkingPointLabel || '';
};

export const getChannelDescription = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return props?.description || '';
};

export const getChannelPerformanceLabel = (channel, showGroupLabel) => {
  const props = CHANNEL_PROPS[channel];
  return (
    (showGroupLabel ? props?.performanceGroupLabel : null) ||
    props?.performanceLabel ||
    props?.label ||
    ''
  );
};

export const getChannelPerformanceShortLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return props?.performanceShortLabel || props?.performanceLabel || props?.label || '';
};

const BACKEND_CHANNEL = {
  hubspot: 'hubspot_emails',
  instagram: 'instagram_account',
  trustpilot: 'reviews_trustpilot',
  amazon: 'reviews_amazon',
};

const BACKEND_CHANNELS_MAPPER = {
  [CHANNEL.hubspot]: BACKEND_CHANNEL.hubspot,
  [CHANNEL.instagram]: BACKEND_CHANNEL.instagram,
  [CHANNEL.trustpilot]: BACKEND_CHANNEL.trustpilot,
  [CHANNEL.amazon]: BACKEND_CHANNEL.amazon,
};

const FRONTEND_CHANNELS_MAPPER = {
  [BACKEND_CHANNEL.hubspot]: CHANNEL.hubspot,
  [BACKEND_CHANNEL.instagram]: CHANNEL.instagram,
  [BACKEND_CHANNEL.trustpilot]: CHANNEL.trustpilot,
  [BACKEND_CHANNEL.amazon]: CHANNEL.amazon,
};

export const alignChannelWithBackend = (channel) => BACKEND_CHANNELS_MAPPER[channel] || channel;

export const alignChannelFromBackend = (channel) => FRONTEND_CHANNELS_MAPPER[channel] || channel;

export const alignChannelForUnauthorize = (channel) =>
  Object.values(EMAILS_CHANNEL).includes(channel)
    ? BACKEND_CHANNEL.hubspot
    : [CHANNEL.facebookPages, CHANNEL.instagram].includes(channel)
    ? CHANNEL.facebook
    : channel;
